import { Giustificativo } from "@/api/openapi";
import { clsx } from "clsx/lite";
// eslint-disable-next-line max-len
import { useAttendanceDesktopGiustificativoRow } from "./attendanceDesktopGiustificativoRow/useAttendanceDesktopGiustificativoRow";
import { TempAttendance } from "@/config/types";
import { Dispatch, FocusEventHandler, SetStateAction } from "react";
import {
  formatHours,
  onChangeTempAttendanceMonth,
} from "../../../attendanceUtils";
import TextButton from "@/components/textButton/TextButton";
import { showTranslated, translate } from "@/config/translator";
import {
  makeCellId,
  onMouseEnterCell,
  onMouseLeaveCell,
} from "./useEmployeeAttendanceRow";

type AttendanceDesktopGiustificativoRowProps = {
  giustificativoIndex: number;
  giustificativo: Giustificativo;
  employeeAttendance: TempAttendance;
  employeeIndex: number;
  onBlurGiustificativoInput: FocusEventHandler<HTMLInputElement>;
  isSaving: boolean;
  setCurrentEditingValue: Dispatch<SetStateAction<string | null>>;
  collapsibleCellClasses: string;
  openRemoveGiustificativoConfirmation: () => void;
  isExpanded: boolean;
};

export function AttendanceDesktopGiustificativoRow({
  giustificativoIndex,
  giustificativo,
  employeeAttendance,
  employeeIndex,
  isSaving,
  onBlurGiustificativoInput,
  setCurrentEditingValue,
  collapsibleCellClasses,
  openRemoveGiustificativoConfirmation,
  isExpanded,
}: AttendanceDesktopGiustificativoRowProps) {
  const { giustificativoSum, setTempAttendanceMonth, isSubmitted, lang } =
    useAttendanceDesktopGiustificativoRow({
      employeeAttendance,
      giustificativo,
    });

  return (
    <tr
      className={clsx(isExpanded ? "table-row" : "none", "h-12", "bg-primary")}
    >
      {/* GIUSTIFICATIVO TYPE */}
      <td
        className={clsx(
          "sticky",
          "relative",
          "left-0",
          "z-10",
          "p-0",
          "border-r",
          "border-neutral",
          "bg-white",
        )}
        id={makeCellId(employeeIndex, -2, giustificativoIndex)}
        onMouseEnter={() =>
          onMouseEnterCell(
            employeeIndex,
            -2,
            giustificativoIndex,
            employeeIndex,
            employeeAttendance.days[0].giustificativi.length,
          )
        }
        onMouseLeave={() =>
          onMouseLeaveCell(
            employeeIndex,
            -2,
            giustificativoIndex,
            employeeIndex,
            employeeAttendance.days[0].giustificativi.length,
          )
        }
      >
        <div
          className={clsx(
            "absolute",
            "left-0",
            "h-full",
            "w-0.5",
            "bg-primary",
          )}
        />
        <div className={clsx(collapsibleCellClasses, "justify-center", "h-12")}>
          {giustificativo.type.toUpperCase()}
        </div>
      </td>

      {/* GIUSTIFICATIVO NAME */}
      <td
        className={clsx(
          "sticky",
          "left-14",
          "z-10",
          "p-0",
          "shadow-md",
          "border-r",
          "border-neutral",
          "bg-white",
        )}
        id={makeCellId(employeeIndex, -1, giustificativoIndex)}
        onMouseEnter={() =>
          onMouseEnterCell(
            employeeIndex,
            -1,
            giustificativoIndex,
            employeeIndex,
            employeeAttendance.days[0].giustificativi.length,
          )
        }
        onMouseLeave={() =>
          onMouseLeaveCell(
            employeeIndex,
            -1,
            giustificativoIndex,
            employeeIndex,
            employeeAttendance.days[0].giustificativi.length,
          )
        }
      >
        <div className={clsx(collapsibleCellClasses, "justify-start", "h-12")}>
          {showTranslated(giustificativo.description, lang)}
        </div>
      </td>

      {/* GIUSTIFICATIVO HOURS PER DAY */}
      {employeeAttendance.days.map((day, dayIndex) => (
        <td
          // eslint-disable-next-line max-len
          key={`giustif.-day-${employeeAttendance.employee.matricola}-${giustificativo.type}-${dayIndex}`}
          id={makeCellId(employeeIndex, dayIndex, giustificativoIndex)}
          onMouseEnter={() =>
            onMouseEnterCell(
              employeeIndex,
              dayIndex,
              giustificativoIndex,
              employeeIndex,
              employeeAttendance.days[0].giustificativi.length,
            )
          }
          onMouseLeave={() =>
            onMouseLeaveCell(
              employeeIndex,
              dayIndex,
              giustificativoIndex,
              employeeIndex,
              employeeAttendance.days[0].giustificativi.length,
            )
          }
          className={clsx(
            "p-0",
            day.isHoliday ? "bg-canvas-secondary" : "bg-white",
            dayIndex !== employeeAttendance.days.length - 1 && "border-r",
            "border-neutral",
          )}
        >
          <div
            className={clsx(
              collapsibleCellClasses,
              "p-0",
              "h-12",
              "justify-center",
              "min-w-14",
              "flex",
              "justify-center",
              "items-center",
            )}
          >
            <input
              disabled={isSaving || isSubmitted === undefined || isSubmitted}
              onFocus={(event) => setCurrentEditingValue(event.target.value)}
              onBlur={onBlurGiustificativoInput}
              className={clsx(
                "w-10",
                "outline-none",
                "rounded-sm",
                "text-center",
                isSaving && "text-disabled",
              )}
              onChange={(event) =>
                onChangeTempAttendanceMonth(
                  setTempAttendanceMonth,
                  employeeAttendance.employee.matricola,
                  employeeAttendance.employee.filiale,
                  dayIndex,
                  event.target.value,
                  giustificativo.type,
                )
              }
              value={(
                day.giustificativi.find((g) => g.type === giustificativo.type)
                  ?.hours ?? ""
              ).replace(".", ",")}
            />
          </div>
        </td>
      ))}

      {/* GIUSTIFICATIVO SUM */}
      <td
        className={clsx(
          "sticky",
          "min-w-16",
          "right-30",
          "top-0",
          "z-10",
          "p-0",
          "border-l",
          "border-neutral",
          "bg-white",
          "shadow-md",
        )}
      >
        <div className={clsx(collapsibleCellClasses, "justify-center", "h-12")}>
          {formatHours(giustificativoSum)}
        </div>
      </td>

      {/* REMOVE GIUSTIFICATIVO */}
      <td
        className={clsx(
          "sticky",
          "right-0",
          "z-10",
          "min-w-30",
          "top-0",
          "p-0",
          "border-l",
          "border-r",
          "border-neutral",
          "bg-white",
        )}
      >
        <div
          className={clsx(
            collapsibleCellClasses,
            "h-12",
            "flex",
            "items-center",
            "justify-center",
          )}
        >
          <TextButton onClick={openRemoveGiustificativoConfirmation}>
            {translate("remove", lang)}
          </TextButton>
        </div>
      </td>
    </tr>
  );
}
